import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

export type CanonicalTreeAncestryReconnectionIntent =
  | 'CONNECT_EXPLICIT_SIBLINGS'
  | 'SET_EXPLICIT_SUBJECT_PARENTS'
  | 'ADD_SUBJECT_AS_PARENT';

export type CanonicalTreeAncestryReconnectionStatus =
  | 'AUTHORIZED_PARENT_EDGE_PLAN'
  | 'AUTHORIZED_EXPLICIT_SIBLING_EDGE_PLAN'
  | 'BLOCKED_MISSING_SUBJECT'
  | 'BLOCKED_MISSING_ANCHOR'
  | 'BLOCKED_MISSING_EXPLICIT_PARENT'
  | 'BLOCKED_MISSING_MEMBER'
  | 'BLOCKED_SELF_PARENT'
  | 'BLOCKED_CROSS_GRAPH'
  | 'BLOCKED_CYCLE'
  | 'BLOCKED_UNSUPPORTED_INTENT';

export interface CanonicalTreeAncestryReconnectionInput {
  readonly members:
    readonly GraphMember[];
  readonly subjectMemberId: string;
  readonly intent:
    CanonicalTreeAncestryReconnectionIntent;
  readonly anchorMemberId?: string | null;
  readonly explicitParentIds?:
    readonly string[];
}

export interface CanonicalTreeParentEdgePlan {
  readonly targetMemberId: string;
  readonly proposedParentIds:
    readonly string[];
  readonly addedParentIds:
    readonly string[];
  readonly preservedParentIds:
    readonly string[];
}

export interface CanonicalTreeExplicitSiblingEdgePlan {
  readonly sourceMemberId: string;
  readonly targetMemberId: string;
  readonly createsAncestry: false;
  readonly inheritsGeneration: true;
  readonly requiresSharedParentFabrication: false;
}

export type CanonicalTreeAncestryReconnectionResult =
  | {
      readonly authorized: true;
      readonly status:
        'AUTHORIZED_PARENT_EDGE_PLAN';
      readonly parentEdgePlan:
        CanonicalTreeParentEdgePlan;
      readonly siblingEdgePlan: null;
      readonly mutatesCanonicalGraph: false;
      readonly reason: string;
    }
  | {
      readonly authorized: true;
      readonly status:
        'AUTHORIZED_EXPLICIT_SIBLING_EDGE_PLAN';
      readonly parentEdgePlan: null;
      readonly siblingEdgePlan:
        CanonicalTreeExplicitSiblingEdgePlan;
      readonly mutatesCanonicalGraph: false;
      readonly reason: string;
    }
  | {
      readonly authorized: false;
      readonly status:
        Exclude<
          CanonicalTreeAncestryReconnectionStatus,
          | 'AUTHORIZED_PARENT_EDGE_PLAN'
          | 'AUTHORIZED_EXPLICIT_SIBLING_EDGE_PLAN'
        >;
      readonly parentEdgePlan: null;
      readonly siblingEdgePlan: null;
      readonly mutatesCanonicalGraph: false;
      readonly reason: string;
    };

const activeMembers = (
  members: readonly GraphMember[],
): readonly GraphMember[] =>
  members.filter(
    member =>
      member.approvalStatus !== 'rejected' &&
      member.profileState !== 'deleted',
  );

const uniqueIds = (
  ids: readonly string[],
): string[] =>
  Array.from(
    new Set(
      ids.filter(Boolean),
    ),
  );

const blocked = (
  status:
    Exclude<
      CanonicalTreeAncestryReconnectionStatus,
      | 'AUTHORIZED_PARENT_EDGE_PLAN'
      | 'AUTHORIZED_EXPLICIT_SIBLING_EDGE_PLAN'
    >,
  reason: string,
): CanonicalTreeAncestryReconnectionResult =>
  Object.freeze({
    authorized: false,
    status,
    parentEdgePlan: null,
    siblingEdgePlan: null,
    mutatesCanonicalGraph: false,
    reason,
  });

const descendantsOf = (
  memberId: string,
  members:
    ReadonlyMap<string, GraphMember>,
): ReadonlySet<string> => {
  const childrenByParent =
    new Map<string, Set<string>>();

  for (const member of members.values()) {
    for (
      const parentId of
        member.parentIds ?? []
    ) {
      const children =
        childrenByParent.get(parentId) ??
        new Set<string>();

      children.add(member.id);
      childrenByParent.set(
        parentId,
        children,
      );
    }
  }

  const descendants =
    new Set<string>();

  const queue = [
    ...(
      childrenByParent.get(memberId) ??
      []
    ),
  ];

  while (queue.length > 0) {
    const current = queue.shift()!;

    if (descendants.has(current)) {
      continue;
    }

    descendants.add(current);

    for (
      const childId of
        childrenByParent.get(current) ??
        []
    ) {
      if (!descendants.has(childId)) {
        queue.push(childId);
      }
    }
  }

  return descendants;
};

const authorizeParentPlan = (
  target: GraphMember,
  requestedParentIds:
    readonly string[],
  members:
    ReadonlyMap<string, GraphMember>,
): CanonicalTreeAncestryReconnectionResult => {
  const proposedParentIds =
    uniqueIds([
      ...(target.parentIds ?? []),
      ...requestedParentIds,
    ]);

  const requested =
    uniqueIds(requestedParentIds);

  if (requested.length === 0) {
    return blocked(
      'BLOCKED_MISSING_EXPLICIT_PARENT',
      'At least one explicit parent identity is required.',
    );
  }

  if (
    requested.includes(target.id)
  ) {
    return blocked(
      'BLOCKED_SELF_PARENT',
      'A member cannot be their own parent.',
    );
  }

  for (const parentId of requested) {
    const parent =
      members.get(parentId);

    if (!parent) {
      return blocked(
        'BLOCKED_MISSING_MEMBER',
        `Explicit parent ${parentId} is not present in the active graph.`,
      );
    }

    if (
      parent.ownerUserId !==
      target.ownerUserId
    ) {
      return blocked(
        'BLOCKED_CROSS_GRAPH',
        'Parent and child must belong to the same canonical graph.',
      );
    }
  }

  const targetDescendants =
    descendantsOf(
      target.id,
      members,
    );

  if (
    requested.some(parentId =>
      targetDescendants.has(parentId),
    )
  ) {
    return blocked(
      'BLOCKED_CYCLE',
      'The proposed parent edge would create a canonical ancestry cycle.',
    );
  }

  const preservedParentIds =
    uniqueIds(
      target.parentIds ?? [],
    );

  const addedParentIds =
    requested.filter(
      parentId =>
        !preservedParentIds.includes(
          parentId,
        ),
    );

  return Object.freeze({
    authorized: true,
    status:
      'AUTHORIZED_PARENT_EDGE_PLAN',
    parentEdgePlan:
      Object.freeze({
        targetMemberId:
          target.id,
        proposedParentIds:
          Object.freeze(
            proposedParentIds,
          ),
        addedParentIds:
          Object.freeze(
            addedParentIds,
          ),
        preservedParentIds:
          Object.freeze(
            preservedParentIds,
          ),
      }),
    siblingEdgePlan: null,
    mutatesCanonicalGraph: false,
    reason:
      'Explicit canonical parent identities produced an additive parent-edge plan.',
  });
};

export const planCanonicalTreeAncestryReconnection = (
  input:
    CanonicalTreeAncestryReconnectionInput,
): CanonicalTreeAncestryReconnectionResult => {
  const members =
    new Map(
      activeMembers(input.members).map(
        member =>
          [member.id, member] as const,
      ),
    );

  const subject =
    members.get(
      input.subjectMemberId,
    );

  if (!subject) {
    return blocked(
      'BLOCKED_MISSING_SUBJECT',
      'The subject member is not present in the active graph.',
    );
  }

  switch (input.intent) {
    case 'SET_EXPLICIT_SUBJECT_PARENTS':
      return authorizeParentPlan(
        subject,
        input.explicitParentIds ?? [],
        members,
      );

    case 'ADD_SUBJECT_AS_PARENT': {
      const anchorId =
        input.anchorMemberId ?? null;

      if (!anchorId) {
        return blocked(
          'BLOCKED_MISSING_ANCHOR',
          'The descendant anchor is required.',
        );
      }

      const anchor =
        members.get(anchorId);

      if (!anchor) {
        return blocked(
          'BLOCKED_MISSING_ANCHOR',
          'The descendant anchor is not present in the active graph.',
        );
      }

      return authorizeParentPlan(
        anchor,
        [subject.id],
        members,
      );
    }

    case 'CONNECT_EXPLICIT_SIBLINGS': {
      const anchorId =
        input.anchorMemberId ?? null;

      if (!anchorId) {
        return blocked(
          'BLOCKED_MISSING_ANCHOR',
          'The sibling anchor is required.',
        );
      }

      const anchor =
        members.get(anchorId);

      if (!anchor) {
        return blocked(
          'BLOCKED_MISSING_ANCHOR',
          'The sibling anchor is not present in the active graph.',
        );
      }

      if (
        anchor.id === subject.id
      ) {
        return blocked(
          'BLOCKED_SELF_PARENT',
          'A member cannot be connected as their own sibling.',
        );
      }

      if (
        anchor.ownerUserId !==
        subject.ownerUserId
      ) {
        return blocked(
          'BLOCKED_CROSS_GRAPH',
          'Explicit siblings must belong to the same canonical graph.',
        );
      }

      const anchorParentIds =
        uniqueIds(
          anchor.parentIds ?? [],
        );

      if (
        anchorParentIds.length > 0
      ) {
        return authorizeParentPlan(
          subject,
          anchorParentIds,
          members,
        );
      }

      return Object.freeze({
        authorized: true,
        status:
          'AUTHORIZED_EXPLICIT_SIBLING_EDGE_PLAN',
        parentEdgePlan: null,
        siblingEdgePlan:
          Object.freeze({
            sourceMemberId:
              subject.id,
            targetMemberId:
              anchor.id,
            createsAncestry: false,
            inheritsGeneration: true,
            requiresSharedParentFabrication:
              false,
          }),
        mutatesCanonicalGraph: false,
        reason:
          'Explicit sibling confirmation is preserved as a lateral canonical workflow because shared parents are not yet known.',
      });
    }

    default:
      return blocked(
        'BLOCKED_UNSUPPORTED_INTENT',
        'The requested ancestry reconnection intent is unsupported.',
      );
  }
};
